import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { LocaleDurationPipe } from '@app/pipes/locale-duration.pipe';
import { TimePipe } from '@app/pipes/time.pipe';
import { DashboardFilterType } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilter } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { TimesheetModel, TimesheetStatus } from '@reducers/orm/timesheet/timesheet.model';
import {
  getTimesheetsForAuthenticatedUserWithinPeriod,
  TimesheetService,
} from '@reducers/orm/timesheet/timesheet.service';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconCheckCircleRegular } from '@sb/mgc-icons/mgcIcon-check_circle_regular.icon';
import { mgcIconCloseCircleRegular } from '@sb/mgc-icons/mgcIcon-close_circle_regular.icon';
import { mgcIconMore3Regular } from '@sb/mgc-icons/mgcIcon-more_3_regular.icon';
import { mgcIconStopwatchCuteRe } from '@sb/mgc-icons/mgcIcon-stopwatch_cute_re.icon';
import { mgcIconTimeDurationRegular } from '@sb/mgc-icons/mgcIcon-time_duration_regular.icon';
import { DashboardWidgetSelectComponent } from '@sb/ui/dashboard-widget/dashboard-widget-select.component';
import { DashboardWidgetComponent } from '@sb/ui/dashboard-widget/dashboard.widget.component';
import { Icon, IconComponent, IconRegistry } from '@sb/ui/icon';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { subWeeks } from 'date-fns';
import { finalize } from 'rxjs/operators';

const FILTER_TYPE = DashboardFilterType.MY_TIMESHEETS;
const FORMATTED_CURRENT_DATE_STRING = toISODateString(new Date());

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'my-timesheet-widget',
  templateUrl: './my-timesheet-widget.component.html',
  imports: [
    DashboardWidgetComponent,
    TranslatePipe,
    DashboardWidgetSelectComponent,
    LocaleDatePipe,
    TimePipe,
    LocaleDurationPipe,
    IconComponent,
  ],
})
export class MyTimesheetWidgetComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private timesheetService = inject(TimesheetService);
  private pageFilterService = inject(PageFiltersService);
  private iconRegistry = inject(IconRegistry);
  private translate = inject(TranslateService);
  private router = inject(Router);

  iconMapper: Record<TimesheetStatus, Icon> = {
    [TimesheetStatus.APPROVED]: mgcIconCheckCircleRegular.name,
    [TimesheetStatus.PENDING]: mgcIconMore3Regular.name,
    [TimesheetStatus.DECLINED]: mgcIconTimeDurationRegular.name,
  };

  periodOptions = [
    { label: this.translate.instant('week'), value: 1 },
    { label: this.translate.instant('2 weeks'), value: 2 },
    { label: this.translate.instant('3 weeks'), value: 3 },
    { label: this.translate.instant('4 weeks'), value: 4 },
  ];

  timesheets = computed<TimesheetModel[]>(() =>
    this.store.selectSignal(
      getTimesheetsForAuthenticatedUserWithinPeriod(
        toISODateString(subWeeks(new Date(), this.widgetPeriod())),
        FORMATTED_CURRENT_DATE_STRING,
      ),
    )(),
  );

  userId = this.store.selectSignal(selectAuthUserId);
  dashboardFilter = this.store.selectSignal(selectAllowedDashboardFilter(FILTER_TYPE));
  isLoading = signal(true);
  widgetPeriod = computed(() => this.dashboardFilter()?.widgetPeriod ?? 1);

  constructor() {
    this.iconRegistry.registerIcons([
      mgcIconStopwatchCuteRe,
      mgcIconCheckCircleRegular,
      mgcIconCloseCircleRegular,
      mgcIconMore3Regular,
      mgcIconTimeDurationRegular,
    ]);
  }

  onPeriodChange(period: number | string) {
    this.pageFilterService.setFilter('dashboard', FILTER_TYPE, {
      widgetPeriod: period,
    });
  }

  ngOnInit() {
    void this.timesheetService
      .load({
        userId: this.userId(),
        minDate: toISODateString(subWeeks(new Date(), 4)),
        maxDate: FORMATTED_CURRENT_DATE_STRING,
        rates: true,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  openTimesheetModal() {
    void this.router.navigate(['', { outlets: { modal: ['timesheet', { date: FORMATTED_CURRENT_DATE_STRING }] } }]);
  }
}
